import { useWorker } from "../utils/workerContext";
import { buildServiceUrl } from "../utils/urlBuilder";
import { useAuthRoles } from "./useAuthRoles";
import { runtimeEnv } from "../utils/runtimeEnv";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function parseMaybeJson(value) {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function extractStatusCode(payload) {
  const parsedPayload = parseMaybeJson(payload);

  if (typeof parsedPayload === "string") {
    const match = parsedPayload.match(/\b([1-5]\d{2})\b/);
    return match ? Number(match[1]) : undefined;
  }

  if (!parsedPayload || typeof parsedPayload !== "object") {
    return undefined;
  }

  const status =
    parsedPayload.statusCode ??
    parsedPayload.status ??
    parsedPayload.data?.statusCode ??
    parsedPayload.response?.status ??
    parsedPayload.response?.data?.statusCode;

  if (status !== undefined && status !== null) {
    return Number(status);
  }

  return (
    extractStatusCode(parsedPayload.body) ??
    extractStatusCode(parsedPayload.error)
  );
}

function buildCallLabel(method, path) {
  const normalizedMethod = String(method || "GET").toUpperCase();
  const normalizedPath = getFirstPathSegment(path);

  if (!normalizedPath) {
    return normalizedMethod;
  }

  return `${normalizedMethod} ${normalizedPath}`;
}

const TECHNICAL_PATH_SEGMENTS = new Set([
  "api",
  "v1",
  "connector",
  "master",
  "consumer",
  "provider",
  "worker-consumer",
  "worker-provider",
]);

function getFirstPathSegment(path) {
  const rawPath = String(path || "").trim();

  if (!rawPath) {
    return "";
  }

  let pathname = rawPath;

  try {
    pathname = new URL(rawPath).pathname;
  } catch {
    pathname = rawPath;
  }

  const firstSegment = pathname
    .split("/")
    .filter(Boolean)
    .find((segment) => !TECHNICAL_PATH_SEGMENTS.has(segment.toLowerCase()));

  return firstSegment ? `/${firstSegment}` : "";
}

function notifyServerError(payload, method, path, t) {
  const statusCode = extractStatusCode(payload);

  if (!statusCode || statusCode < 500 || statusCode >= 600) {
    return;
  }

  const callLabel = buildCallLabel(method, path);
  toast.error(
    t("toaster.serverError", {
      statusCode,
      call: callLabel,
      defaultValue: "Server error {{statusCode}} in {{call}}",
    }),
    {
      toastId: `server-error-${statusCode}-${callLabel}`,
    },
  );
}

export const useWorkerApiHelper = () => {
  const worker = useWorker();
  const { token, preferredConnectorService } = useAuthRoles();
  const { t } = useTranslation();

  const callApiWithWorker = (
    path,
    {
      connector = preferredConnectorService,
      method = "GET",
      body = null,
      headers = {},
      cacheMode,
      cacheMaxAgeMs,
      useRuntimePort,
      useRoleBasedConnectorRouting,
    } = {},
  ) => {
    return new Promise((resolve, reject) => {
      if (!worker) return reject(new Error("Worker not initialized"));

      const requestId = crypto.randomUUID();
      const endpoint = buildServiceUrl(connector, path, {
        token,
        useRuntimePort,
        useRoleBasedConnectorRouting,
      });

      let settled = false;

      const cleanup = () => {
        worker.removeEventListener("message", handleMessage);
        clearTimeout(timeoutId);
      };

      const safeResolve = (val) => {
        if (settled) return;
        settled = true;
        cleanup();
        resolve(val);
      };

      const safeReject = (err) => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(err);
      };

      const handleMessage = (event) => {
        const { type, result, id, error } = event?.data || {};
        if (id !== requestId) return;

        if (type === "RESPONSE_SUCCESS" || type === "RESPONSE_FROM_CACHE") {
          notifyServerError(result, method, path, t);
          safeResolve(result);
          return;
        }

        if (type === "RESPONSE_ERROR") {
          if (
            method === "DELETE" &&
            String(error).includes("Unexpected end of JSON input")
          ) {
            safeResolve({ statusCode: 204, body: "" });
            return;
          }

          try {
            const parsedError = JSON.parse(error);
            notifyServerError(parsedError, method, path, t);
            safeReject(parsedError);
          } catch {
            const fallbackError = { error };
            notifyServerError(fallbackError, method, path, t);
            safeReject(fallbackError);
          }
        }
      };

      worker.addEventListener("message", handleMessage);

      worker.postMessage({
        type: "SET_CONFIG",
        config: {
          genericEndpoint: endpoint,
          jwt: token,
          hmacSecret: runtimeEnv.REACT_APP_HMAC_SECRET,
        },
      });

      worker.postMessage({
        type: "NEW_HTTP_REQUEST",
        endpoint,
        method,
        body,
        headers,
        requestId,
        cacheMode,
        cacheMaxAgeMs,
      });

      const timeoutId = setTimeout(() => {
        safeReject(
          new Error(`Worker request timed out (30s): ${method} ${endpoint}`),
        );
      }, 60000);
    });
  };

  return { callApiWithWorker };
};
